import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTemplates, useListPresets, useSupportAgents } from 'hooks/api';
import Flex from 'components/shared-components/Flex';
import {
  Form,
  Input,
  Select,
  AutoComplete,
  Button,
  Space,
  Tag,
  Tabs,
  Typography,
  Tooltip,
  Radio,
  Divider,
  Switch,
  Row,
  Col,
} from 'antd';
import { EyeOutlined, EyeInvisibleOutlined, ArrowRightOutlined } from '@ant-design/icons';
import {
  extractTemplateQuickReplyButtons,
  mergeTemplateButtons,
} from '../../utils/flowTemplateButtons';
import { getButtonLabel } from '../../utils/flowButtons';
import { DEFAULT_CAPTURE_VAR } from '../../utils/incomingCapture';
import NodeSelect from '../NodeSelect';
import {
  routesConfigToList,
  routesListToConfig,
  routesFromEdges,
  suggestBranchValues,
} from '../../utils/routeByVar';
import {
  presetRowsFromPreset,
  mergeListPresetRows,
} from '../../utils/flowListPresetRows';
import {
  AiClassifyConfig,
  AiExtractConfig,
  AiReplyConfig,
  AiGenerateConfig,
} from './AiNodeConfigForms';
import { ListDynamicConfig } from './ListDynamicConfig';
import SelectMediaModel from 'views/app-views/whatsapp/Campaign/CampainComps/SelectMediaModel';
import { MEDIA_KINDS } from 'views/app-views/whatsapp/chat/mediaKindUtils';
import ListItemPreview from 'views/app-views/whatsapp/ListItem/Components/ListItemPreview';
import { getFlows } from 'services/chatbotFlowService';
import { ROW_GUTTER } from 'constants/ThemeConstant';

const { TextArea } = Input;
const { Text } = Typography;

/**
 * Insert `placeholder` at the current cursor position of a textarea/input ref.
 * Prevents the common bug where the variable is appended to the end of the text.
 */
function insertAtCursor(ref, current, placeholder, onComplete) {
  const el = ref.current?.resizableTextArea?.textArea || ref.current;
  const start = el?.selectionStart ?? (current || '').length;
  const end = el?.selectionEnd ?? start;
  const next = (current || '').slice(0, start) + placeholder + (current || '').slice(end);
  onComplete(next);
  setTimeout(() => {
    if (el) { el.selectionStart = el.selectionEnd = start + placeholder.length; el.focus(); }
  }, 0);
}

const CarouselPreview = React.lazy(() =>
  import('views/app-views/whatsapp/Carousel/carouselPreview')
);

export function TriggerKeywordConfig({ config, onChange }) {
  const keywords = config.keywords || [];
  const matchMode = config.match || 'exact';

  // Short keywords (≤3 chars) are always exact in the engine regardless of match mode
  const shortKeywords = keywords.filter((kw) => kw.trim().length <= 3);
  const longKeywords = keywords.filter((kw) => kw.trim().length > 3);
  const hasLongKeyword = longKeywords.length > 0;
  const hasMixed = shortKeywords.length > 0 && longKeywords.length > 0;

  // Show match type dropdown only when at least one keyword is long enough for fuzzy
  const showMatchType = hasLongKeyword;

  return (
    <>
      <Form.Item label="Keywords">
        <Select
          mode="tags"
          value={keywords}
          onChange={(kws) => {
            const hadLong = keywords.some((k) => k.trim().length > 3);
            const hasLong = kws.some((k) => k.trim().length > 3);
            // First time a long keyword is added (Match type dropdown appears),
            // force match to 'exact' so it doesn't reveal a stale backend 'fuzzy'.
            if (hasLong && !hadLong) {
              onChange({ keywords: kws, match: 'exact' });
            } else {
              onChange({ keywords: kws });
            }
          }}
          placeholder="hi, hello, balance"
        />
      </Form.Item>

      {showMatchType && (
        <Form.Item label="Match type">
          <Select
            value={matchMode}
            onChange={(match) => onChange({ match })}
            options={[
              { value: 'fuzzy', label: 'Fuzzy' },
              { value: 'exact', label: 'Exact' },
            ]}
          />
        </Form.Item>
      )}

      {/* Mixed keywords — show which are exact vs fuzzy */}
      {matchMode === 'fuzzy' && hasMixed && (
        <Form.Item>
          <div style={{ fontSize: 12, lineHeight: '1.8', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: '8px 12px' }}>
            <div style={{ fontWeight: 600, color: '#ad6800', marginBottom: 4 }}>
              ⚠ These keywords will not behave the same way:
            </div>
            <div style={{ color: '#595959' }}>
              <span style={{ display: 'inline-block', marginRight: 6 }}>✅</span>
              <strong>Will match similar words too:</strong>{' '}
              {longKeywords.map((k) => (
                <span key={k} style={{ background: '#d9f7be', border: '1px solid #95de64', borderRadius: 4, padding: '1px 6px', marginRight: 4, fontSize: 11, color: '#237804' }}>
                  {k}
                </span>
              ))}
            </div>
            <div style={{ color: '#595959', marginTop: 4 }}>
              <span style={{ display: 'inline-block', marginRight: 6 }}>⚠️</span>
              <strong>Must be typed exactly by the customer:</strong>{' '}
              {shortKeywords.map((k) => (
                <span key={k} style={{ background: '#fff1b8', border: '1px solid #ffd666', borderRadius: 4, padding: '1px 6px', marginRight: 4, fontSize: 11, color: '#ad6800' }}>
                  {k}
                </span>
              ))}
              <span style={{ color: '#8c8c8c' }}> — word is too short to use smart matching</span>
            </div>
          </div>
        </Form.Item>
      )}

      {/* All keywords are short — fuzzy impossible */}
      {matchMode === 'fuzzy' && shortKeywords.length > 0 && !hasMixed && (
        <Form.Item>
          <div style={{ color: '#faad14', fontSize: 12, lineHeight: '1.5' }}>
            ⚠ {shortKeywords.length === 1
              ? `"${shortKeywords[0]}"`
              : shortKeywords.map((k) => `"${k}"`).join(', ')} {shortKeywords.length === 1 ? 'is' : 'are'} very short — the customer must type {shortKeywords.length === 1 ? 'it' : 'them'} exactly for this flow to trigger.
          </div>
        </Form.Item>
      )}

      {!showMatchType && keywords.length > 0 && (
        <Form.Item>
          <div style={{ color: '#8c8c8c', fontSize: 12 }}>
            📌 These keywords are very short — the customer must type them exactly for this flow to trigger.
          </div>
        </Form.Item>
      )}
    </>
  );
}

export function TriggerRegexConfig({ config, onChange }) {
  return (
    <Form.Item label="Regex pattern">
      <Input
        value={config.pattern}
        onChange={(e) => onChange({ pattern: e.target.value })}
        placeholder="^\\d{10}$"
      />
    </Form.Item>
  );
}

const REPLY_MESSAGE_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: 'document', label: 'Document' },
];

export function MessageTextConfig({ config, onChange, placeholders = [] }) {
  const textareaRef = React.useRef(null);
  const captionRef = React.useRef(null);
  const messageType = config.message_type || 'text';
  const isText = messageType === 'text';
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const mediaId = config.media_id || '';
  const mediaName = config.media_name || '';

  const handleMessageTypeChange = (nextType) => {
    if (nextType === messageType) {
      return;
    }
    if (nextType === 'text') {
      onChange({ message_type: 'text', media_id: '', media_name: '' });
      return;
    }
    onChange({
      message_type: nextType,
      media_id: '',
      media_name: '',
    });
  };

  const handleMediaSelect = (id, name) => {
    onChange({
      media_id: id || '',
      media_name: name || '',
    });
    setMediaModalOpen(false);
  };

  const mediaTypeLabel = MEDIA_KINDS[messageType]?.label || messageType;

  return (
    <Row gutter={ROW_GUTTER}>
      <Col span={12}>
        <Form.Item label="Message type">
          <Radio.Group
            value={messageType}
            onChange={(e) => handleMessageTypeChange(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          >
            {REPLY_MESSAGE_TYPE_OPTIONS.map((opt) => (
              <Radio.Button key={opt.value} value={opt.value}>
                {opt.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>
      </Col>
      <>
        {isText ? (
          <Col span={24}>
            <Form.Item label="Message body">
              <TextArea
                ref={textareaRef}
                rows={4}
                value={config.body || config.text}
                onChange={(e) => onChange({ body: e.target.value })}
              />
            </Form.Item>
            <PlaceholderChips
              placeholders={placeholders}
              textareaRef={textareaRef}
              onInsert={(p) =>
                insertAtCursor(textareaRef, config.body || config.text, p, (v) => onChange({ body: v }))
              }
            />
          </Col>
        ) : (
          <>
            <Col span={12}>
              <Form.Item
                label={`${mediaTypeLabel} media`}
                extra="Pick from your media library — the Meta media ID is saved on this step."
                required
              >
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  {mediaId ? (
                    <Space size={4} wrap>
                      <Tag color="blue" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {mediaName || mediaId}
                      </Tag>
                      <Button
                        type="text"
                        danger
                        onClick={() => onChange({ media_id: '', media_name: '' })}
                      >
                        Remove
                      </Button>
                    </Space>
                  ) : null}
                  <Button block type={mediaId ? 'default' : 'primary'} onClick={() => setMediaModalOpen(true)}>
                    {mediaId ? `Change ${mediaTypeLabel.toLowerCase()}` : `Select ${mediaTypeLabel.toLowerCase()}`}
                  </Button>
                  <SelectMediaModel
                    openMediaModel={mediaModalOpen}
                    handleCancelMediaModel={() => setMediaModalOpen(false)}
                    HandleUploadImage={(id, name) => handleMediaSelect(id, name)}
                    requestType="Custom"
                    handleCustomMedia={(id, name) => handleMediaSelect(id, name)}
                    mediaKindFilter={messageType}
                  />
                </Space>
              </Form.Item>
            </Col>
            {messageType !== 'audio' && (
              <Col span={24}>
                <Form.Item label="Caption (optional)">
                  <TextArea
                    ref={captionRef}
                    rows={3}
                    value={config.body || ''}
                    onChange={(e) => onChange({ body: e.target.value })}
                    placeholder="Optional text shown with the media"
                  />
                </Form.Item>
                <PlaceholderChips
                  placeholders={placeholders}
                  textareaRef={captionRef}
                  onInsert={(p) =>
                    insertAtCursor(captionRef, config.body || '', p, (v) => onChange({ body: v }))
                  }
                />
              </Col>
            )}
          </>
        )}
      </>
    </Row>
  );
}

export function CaptureIncomingField({ config, onChange, varOptions = [] }) {
  const value =
    config.capture_var != null && String(config.capture_var).trim() !== ''
      ? String(config.capture_var).trim()
      : undefined;

  return (
    <Form.Item
      label="Store incoming message as"
      extra="Clear to disable storing incoming text on this step."
    >
      <Select
        showSearch
        allowClear
        mode="tags"
        maxCount={1}
        placeholder={`Optional — e.g. ${DEFAULT_CAPTURE_VAR}`}
        value={value ? [value] : []}
        onChange={(vals) => {
          const next = vals?.[0];
          onChange({
            capture_var: next != null && String(next).trim() !== '' ? String(next).trim() : '',
          });
        }}
        options={varOptions}
        notFoundContent={null}
        style={{ width: '100%' }}
      />
    </Form.Item>
  );
}

export function MessageTemplateConfig({ config, onChange, nodeOptions = [], varOptions = [] }) {
  const { templates, isLoading } = useTemplates();
  const [templatePreview, setTemplatePreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const templateType = useMemo(() => {
    if (config.template_type) return config.template_type;
    if (config.template_name && templates.length > 0) {
      const matched = templates.find((t) => t.name === config.template_name);
      if (matched?.components?.some((c) => c.type === 'CAROUSEL')) {
        return 'carousel';
      }
    }
    return 'normal';
  }, [config.template_type, config.template_name, templates]);

  const isCarousel = templateType === 'carousel';

  const templateCategory = useMemo(() => {
    if (config.template_category) return config.template_category;
    if (config.template_name && templates.length > 0) {
      const matched = templates.find((t) => t.name === config.template_name);
      if (matched?.category) {
        return matched.category;
      }
    }
    return 'MARKETING';
  }, [config.template_category, config.template_name, templates]);

  // Mirror the same filtering logic used in templates.js
  const templateOptions = useMemo(() => {
    return templates
      .filter(
        (t) =>
          t.status === 'APPROVED' &&
          t.category === templateCategory &&
          (isCarousel
            ? t.components?.some((c) => c.type === 'CAROUSEL')
            : !t.components?.some((c) => c.type === 'CAROUSEL'))
      )
      .map((t) => ({
        value: t.name,
        label: `${t.name} (${t.language})`,
      }));
  }, [templates, isCarousel, templateCategory]);

  // Sync preview when config.template_name changes externally (e.g. on node open)
  useEffect(() => {
    if (config.template_name && templates.length > 0) {
      const matched = templates.find((t) => t.name === config.template_name);
      if (matched) {
        if (isCarousel) {
          setTemplatePreview(matched);
        } else {
          setTemplatePreview(extractSections(matched));
        }
      } else {
        setTemplatePreview(null);
      }
    } else if (!config.template_name) {
      setTemplatePreview(null);
    }
  }, [config.template_name, templates, isCarousel]);

  const applyTemplateMeta = (matched) => {
    const sections = matched ? extractSections(matched) : null;
    setTemplatePreview(sections);
    const quickReplies = extractTemplateQuickReplyButtons(sections);
    if (quickReplies.length === 0) return {};
    return { buttons: mergeTemplateButtons(quickReplies, config.buttons || []) };
  };

  const handleSelect = (name) => {
    if (!name) {
      onChange({ template_name: name, buttons: [] });
      setTemplatePreview(null);
      setShowPreview(false);
      return;
    }
    const matched = templates.find((t) => t.name === name);
    if (isCarousel) {
      setTemplatePreview(matched);
      onChange({
        template_name: name,
        template_category: matched?.category || templateCategory,
        template_language: matched?.language || 'en_US',
      });
    } else {
      onChange({
        template_name: name,
        template_category: matched?.category || templateCategory,
        template_language: matched?.language || 'en_US',
        ...applyTemplateMeta(matched),
      });
    }
    setShowPreview(true);
  };

  const buttonsRef = useRef(config.buttons);
  buttonsRef.current = config.buttons;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!config.template_name || templates.length === 0) return;
    const matched = templates.find((t) => t.name === config.template_name);
    if (!matched) return;

    // Backfill template_language for nodes saved before it was tracked, or
    // repair a stale code, so the engine sends the language Meta actually
    // approved (e.g. "en") instead of the old "en_US" default.
    if (matched.language && config.template_language !== matched.language) {
      onChangeRef.current({ template_language: matched.language });
    }

    let quickReplies = [];
    if (isCarousel) {
      setTemplatePreview(matched);
      quickReplies = extractCarouselQuickReplyButtons(matched);
    } else {
      const sections = extractSections(matched);
      setTemplatePreview(sections);
      quickReplies = extractTemplateQuickReplyButtons(sections);
    }

    if (quickReplies.length === 0) return;
    const merged = mergeTemplateButtons(quickReplies, buttonsRef.current || []);
    const same =
      merged.length === (buttonsRef.current || []).length &&
      merged.every(
        (b, i) =>
          b.id === buttonsRef.current?.[i]?.id &&
          b.target_node_id === buttonsRef.current?.[i]?.target_node_id
      );
    if (!same) onChangeRef.current({ buttons: merged });
  }, [config.template_name, config.template_language, templates, isCarousel]);

  const buttons = config.buttons || [];
  const quickReplyButtons = isCarousel
    ? extractCarouselQuickReplyButtons(templatePreview)
    : extractTemplateQuickReplyButtons(templatePreview);

  return (
    <Flex gap={24} alignItems="flex-start" style={{ gap: '24px' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Flex alignItems="center" gap={16} style={{ flexWrap: 'wrap' }}>
          <Form.Item label="Template Type">
            <Radio.Group
              value={templateType}
              onChange={(e) => {
                const val = e.target.value;
                onChange({
                  template_type: val,
                  template_name: '',
                  template_category: 'MARKETING',
                  buttons: [],
                });
                setTemplatePreview(null);
                setShowPreview(false);
              }}
            >
              <Radio value="normal">Normal</Radio>
              <Radio value="carousel">Carousel</Radio>
            </Radio.Group>
          </Form.Item>
          <Divider type="vertical" style={{ height: '40px', borderColor: '#00000062' }} />
          <Form.Item label="Template Category">
            <Radio.Group
              value={templateCategory}
              onChange={(e) => {
                onChange({
                  template_category: e.target.value,
                  template_name: '',
                  buttons: [],
                });
                setTemplatePreview(null);
                setShowPreview(false);
              }}
            >
              <Radio value="MARKETING">Marketing</Radio>
              <Radio value="UTILITY">Utility</Radio>
            </Radio.Group>
          </Form.Item>
        </Flex>
        <Form.Item
          label={
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Template name
              {config.template_name && (
                <Tooltip title={showPreview ? 'Hide preview' : 'Show preview'}>
                  <Button
                    type="text"
                    size="small"
                    shape="circle"
                    icon={showPreview ? <EyeOutlined style={{ color: '#3e79f7' }} /> : <EyeInvisibleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />}
                    onClick={() => setShowPreview(!showPreview)}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  />
                </Tooltip>
              )}
            </span>
          }
        >
          <Select
            showSearch
            allowClear
            loading={isLoading}
            placeholder="Search and select a template"
            value={config.template_name || undefined}
            onChange={handleSelect}
            options={templateOptions}
            filterOption={(input, opt) =>
              opt?.label?.toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: '100%' }}
          />
        </Form.Item>
        {!isCarousel && templatePreview?.header &&
          ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(templatePreview.header.format) && (
            <TemplateMediaField
              headerFormat={templatePreview.header.format}
              config={config}
              onChange={onChange}
              varOptions={varOptions}
            />
          )}
        {(quickReplyButtons.length > 0 || config.template_name) && (
          <CaptureIncomingField config={config} onChange={onChange} varOptions={varOptions} />
        )}
        {quickReplyButtons.length > 0 && (
          <Form.Item
            // label="When customer taps a button"
            extra="Incoming text is matched to button id, stored in the variable above, then the target step runs. Save node to wire edges."
            className='mt-2'
          >
            {buttons.map((btn, i) => (
              <div
                key={btn.id || i}
                style={{
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  padding: '8px 12px',
                  marginBottom: 8,
                }}
              >
                <Flex alignItems="center" gap={8} margin="0 0 12px 0" style={{ gap: '8px' }}>
                  <Text strong>{getButtonLabel(btn)}</Text>
                  <ArrowRightOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                  <Text type="secondary">Send this step</Text>
                </Flex>
                <Form.Item style={{ marginBottom: 0 }}>
                  <NodeSelect
                    placeholder="Select text reply or other step"
                    value={btn.target_node_id}
                    onChange={(target_node_id) => {
                      const next = [...buttons];
                      next[i] = { ...next[i], target_node_id };
                      onChange({ buttons: next });
                    }}
                  />
                </Form.Item>
              </div>
            ))}
          </Form.Item>
        )}
      </div>
      {showPreview && templatePreview && (
        <div style={{ flex: 1, minWidth: 320, maxWidth: 400, position: 'sticky', top: 16 }}>
          <TemplatePreview template={templatePreview} isCarousel={isCarousel} />
        </div>
      )}
    </Flex>
  );
}

const MEDIA_SOURCE_OPTIONS = [
  { value: 'id', label: 'Media ID' },
  { value: 'url', label: 'Media URL' },
  { value: 'var', label: 'From variable' },
];

function TemplateMediaField({ headerFormat, config, onChange, varOptions = [] }) {
  const format = headerFormat?.toUpperCase();
  const mediaSource = config.header_media_source || 'id';
  const mediaValue = config.header_media_value || '';
  const mediaName = config.header_media_name || '';
  const [mediaModalOpen, setMediaModalOpen] = useState(false);

  const formatLabel =
    format === 'IMAGE' ? 'Image' : format === 'VIDEO' ? 'Video' : 'Document';

  const handleMediaSelect = (idOrPath, name) => {
    onChange({
      header_media_value: idOrPath || '',
      header_media_name: name || '',
      header_media_source: 'id',
    });
    setMediaModalOpen(false);
  };

  const valueField =
    mediaSource === 'id' ? (
      <Space style={{ width: '100%' }} direction="vertical" size={4}>
        {mediaValue ? (
          <Space size={4}>
            <Tag color="blue" style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {mediaName || mediaValue}
            </Tag>
            <Button type="text" danger onClick={() => onChange({ header_media_value: '', header_media_name: '' })}>
              ×
            </Button>
          </Space>
        ) : null}
        <Button block onClick={() => setMediaModalOpen(true)}>
          {mediaValue ? 'Change' : 'Select media'}
        </Button>
        <SelectMediaModel
          openMediaModel={mediaModalOpen}
          handleCancelMediaModel={() => setMediaModalOpen(false)}
          HandleUploadImage={(path, name) => handleMediaSelect(path, name)}
          requestType="Custom"
          handleCustomMedia={(id, name) => handleMediaSelect(id, name)}
        />
      </Space>
    ) : mediaSource === 'var' ? (
      varOptions.length > 0 ? (
        <Select
          showSearch
          allowClear
          placeholder="Variable"
          value={mediaValue || undefined}
          onChange={(v) => onChange({ header_media_value: v || '' })}
          options={varOptions}
          style={{ width: '100%' }}
        />
      ) : (
        <Input
          value={mediaValue}
          onChange={(e) => onChange({ header_media_value: e.target.value })}
          placeholder="e.g. media_id"
        />
      )
    ) : (
      <Input
        value={mediaValue}
        onChange={(e) => onChange({ header_media_value: e.target.value })}
        placeholder="https://example.com/file"
      />
    );

  return (
    <Flex gap={12} margin="10px 0" style={{ gap: '12px' }}>
      <div style={{ flex: 1 }}>
        <Form.Item
          label={`${formatLabel} header`}
          style={{ marginBottom: 0 }}
        >
          <Select
            value={mediaSource}
            onChange={(v) => onChange({ header_media_source: v, header_media_value: '', header_media_name: '' })}
            options={MEDIA_SOURCE_OPTIONS}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </div>
      <div style={{ flex: 1 }}>
        <Form.Item
          label={mediaSource === 'id' ? 'Library' : mediaSource === 'var' ? 'Variable' : 'URL'}
          style={{ marginBottom: 0 }}
        >
          {valueField}
        </Form.Item>
      </div>
    </Flex>
  );
}

/** Extract header/body/footer/buttons from raw template object */
function extractSections(raw) {
  let header = null, body = null, footer = null, buttons = null;
  raw?.components?.forEach((s) => {
    if (s.type === 'HEADER') header = s;
    else if (s.type === 'BODY') body = s;
    else if (s.type === 'FOOTER') footer = s;
    else if (s.type === 'BUTTONS') buttons = s;
  });
  return { header, body, footer, buttons };
}

function extractCarouselQuickReplyButtons(template) {
  if (!template) return [];
  const carouselComp = template.components?.find((c) => c.type === 'CAROUSEL');
  if (!carouselComp || !carouselComp.cards) return [];

  const buttons = [];
  carouselComp.cards.forEach((card, cardIndex) => {
    const buttonsComp = card.components?.find((c) => c.type === 'BUTTONS');
    if (buttonsComp) {
      const list = buttonsComp.buttons ?? (Array.isArray(buttonsComp) ? buttonsComp : []);
      list.forEach((b, btnIndex) => {
        if (b?.type === 'QUICK_REPLY' && b?.text) {
          buttons.push({
            id: `card_${cardIndex}_btn_${btnIndex}_${b.text}`,
            label: `Card ${cardIndex + 1}: ${b.text}`,
          });
        }
      });
    }
  });
  return buttons;
}

/** Lazy-loaded preview — import NewMobilePreview inline to avoid circular deps */
const NewMobilePreview = React.lazy(() =>
  import('views/app-views/whatsapp/Campaign/newMobilePreview')
);

function TemplatePreview({ template, isCarousel }) {
  if (isCarousel) {
    let mainBodyTemp = null;
    let templateCards = [];
    template?.components?.forEach((section) => {
      if (section.type === 'BODY') {
        mainBodyTemp = section;
      } else if (section.type === 'CAROUSEL') {
        templateCards = (section.cards || []).map((card) => {
          let cardHeader = null;
          let cardBody = null;
          let cardButtons = null;
          card.components?.forEach((cardSection) => {
            if (cardSection.type === 'HEADER') cardHeader = cardSection;
            else if (cardSection.type === 'BODY') cardBody = cardSection;
            else if (cardSection.type === 'BUTTONS') cardButtons = cardSection;
          });
          return { header: cardHeader, body: cardBody, buttons: cardButtons };
        });
      }
    });

    return (
      <div style={{ marginTop: 16 }}>
        <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
          Carousel Preview
        </Text>
        <React.Suspense fallback={null}>
          <CarouselPreview
            isLoop={false}
            mainBody={mainBodyTemp}
            cards={templateCards}
            requiredMedia={false}
          />
        </React.Suspense>
      </div>
    );
  }

  return (
    <div>
      <React.Suspense fallback={null}>
        <NewMobilePreview
          requestType="template"
          template={template}
          mediaRequired={false}
        />
      </React.Suspense>
    </div>
  );
}


export function LogicAskConfig({ config, onChange, varOptions = [], placeholders = [] }) {
  const promptRef = React.useRef(null);
  const invalidRef = React.useRef(null);

  const insertAt = (ref, field, current, p) =>
    insertAtCursor(ref, current, p, (v) => onChange({ [field]: v }));

  return (
    <>
      <Form.Item label="Prompt text">
        <TextArea
          ref={promptRef}
          rows={3}
          value={config.prompt_text}
          onChange={(e) => onChange({ prompt_text: e.target.value })}
        />
      </Form.Item>
      <PlaceholderChips
        placeholders={placeholders}
        textareaRef={promptRef}
        onInsert={(p) => insertAt(promptRef, 'prompt_text', config.prompt_text, p)}
      />
      <Form.Item
        label="Variable name"
        extra="Stored as {{vars.name}} — used in API URLs and conditions"
      >
        <AutoComplete
          placeholder="otp"
          value={config.var ?? ''}
          onChange={(v) => onChange({ var: v })}
          options={varOptions}
          filterOption={(input, option) =>
            option.value.toLowerCase().includes(input.toLowerCase())
          }
          allowClear
        />
      </Form.Item>
      <Form.Item label="Invalid reply">
        <Input
          ref={invalidRef}
          value={config.invalid_reply_text}
          onChange={(e) => onChange({ invalid_reply_text: e.target.value })}
        />
      </Form.Item>
      <PlaceholderChips
        placeholders={placeholders}
        textareaRef={invalidRef}
        onInsert={(p) => insertAt(invalidRef, 'invalid_reply_text', config.invalid_reply_text, p)}
      />
    </>
  );
}

export function RouteByVarConfig({
  config,
  onChange,
  varOptions = [],
  nodeOptions = [],
  nodeId = null,
  definition = null,
}) {
  const currentVar = (config.var || 'branch').trim() || 'branch';
  const selectOptions =
    currentVar && !varOptions.some((o) => o.value === currentVar)
      ? [{ value: currentVar, label: currentVar }, ...varOptions]
      : varOptions;

  const routes = useMemo(() => routesConfigToList(config.routes), [config.routes]);

  const wrongCompletedCount = useMemo(() => {
    if (!nodeId || !definition?.edges) return 0;
    const routeTargets = new Set(routes.map((r) => r.target_node_id).filter(Boolean));
    return definition.edges.filter(
      (e) =>
        e.from === nodeId &&
        e.event === 'completed' &&
        (routeTargets.size === 0 || routeTargets.has(e.to))
    ).length;
  }, [nodeId, definition, routes]);

  // Hydrate routes from graph edges when config is empty (e.g. after JSON import)
  useEffect(() => {
    if (!nodeId || !definition || routes.length > 0) return;
    const fromEdges = routesFromEdges(definition, nodeId);
    if (fromEdges.length > 0) {
      onChange({ routes: routesListToConfig(fromEdges) });
    }
  }, [nodeId, definition]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRoutes = (nextList) => {
    onChange({ routes: routesListToConfig(nextList) });
  };

  const updateRoute = (index, patch) => {
    const next = routes.map((r, i) => (i === index ? { ...r, ...patch } : r));
    setRoutes(next);
  };

  const addRoute = () => {
    const suggestions = suggestBranchValues(definition, currentVar);
    const used = new Set(routes.map((r) => r.branch.toLowerCase()));
    const branch = suggestions.find((s) => !used.has(s.toLowerCase())) || '';
    setRoutes([...routes, { branch, target_node_id: '' }]);
  };

  const removeRoute = (index) => {
    setRoutes(routes.filter((_, i) => i !== index));
  };

  const fillFromTemplate = () => {
    const suggestions = suggestBranchValues(definition, currentVar);
    if (!suggestions.length) return;
    const existing = new Map(routes.map((r) => [r.branch.toLowerCase(), r]));
    const merged = [...routes];
    suggestions.forEach((branch) => {
      if (!existing.has(branch.toLowerCase())) {
        merged.push({ branch, target_node_id: '' });
      }
    });
    setRoutes(merged);
  };

  const templateSuggestions = suggestBranchValues(definition, currentVar);

  return (
    <>
      <Form.Item
        label="Variable to route on"
        extra="Usually the variable saved when the customer tapped a template button (e.g. branch)."
      >
        {varOptions.length > 0 ? (
          <Select
            showSearch
            allowClear
            value={currentVar || undefined}
            onChange={(val) => onChange({ var: val ?? 'branch' })}
            options={selectOptions}
            placeholder="branch"
          />
        ) : (
          <Input
            value={currentVar}
            onChange={(e) => onChange({ var: e.target.value })}
            placeholder="branch"
          />
        )}
      </Form.Item>

      {wrongCompletedCount > 0 && (
        <div
          style={{
            marginBottom: 16,
            padding: '8px 12px',
            background: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          This node has {wrongCompletedCount} <strong>completed</strong> link(s) to next steps.
          Route branches must use <strong>route</strong> links. Configure below and click{' '}
          <strong>Save node</strong> to fix.
        </div>
      )}

      <Form.Item
        label="Route branches"
        extra={
          <>
            When <code>vars.{currentVar || 'branch'}</code> equals the value, send that step.
            Click <strong>Save node</strong> to wire edges (same as template buttons).
          </>
        }
      >
        {templateSuggestions.length > 0 && routes.length < templateSuggestions.length && (
          <Button type="link" size="small" style={{ padding: 0, marginBottom: 8 }} onClick={fillFromTemplate}>
            Add routes from template buttons ({templateSuggestions.join(', ')})
          </Button>
        )}
        {routes.map((route, i) => (
          <div
            key={`route-${i}`}
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: '8px 12px',
              marginBottom: 8,
            }}
          >
            <Flex alignItems="center" gap={8} style={{ gap: 8, marginBottom: 8 }}>
              <Text strong style={{ flex: 1 }}>
                When {currentVar} equals
              </Text>
              <Button type="text" danger size="small" onClick={() => removeRoute(i)}>
                Remove
              </Button>
            </Flex>
            <Form.Item label="Value" style={{ marginBottom: 8 }}>
              <Input
                placeholder="e.g. I want more services"
                value={route.branch}
                onChange={(e) => updateRoute(i, { branch: e.target.value })}
              />
            </Form.Item>
            <Flex alignItems="center" gap={8} style={{ gap: 8, marginBottom: 4 }}>
              <ArrowRightOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
              <Text type="secondary">Send this step</Text>
            </Flex>
            <NodeSelect
              placeholder="Select message step"
              value={route.target_node_id}
              onChange={(target_node_id) => updateRoute(i, { target_node_id: target_node_id || '' })}
            />
          </div>
        ))}
        <Button type="dashed" block onClick={addRoute}>
          Add route branch
        </Button>
      </Form.Item>
    </>
  );
}

const COMMON_REGEX_PATTERNS = [
  { label: '6-digit OTP', value: '^[0-9]{6}$' },
  { label: 'Email Address', value: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$' },
  { label: '10-digit Phone', value: '^[0-9]{10}$' },
  { label: 'Numbers Only', value: '^[0-9]+$' },
  { label: 'Alphabet Only', value: '^[a-zA-Z]+$' },
];

export function LogicValidateConfig({ config, onChange, varOptions = [] }) {
  return (
    <>
      <Form.Item label="Variable">
        {varOptions.length > 0 ? (
          <Select
            value={config.var}
            onChange={(v) => onChange({ var: v })}
            options={varOptions}
            placeholder="Select variable from flow"
          />
        ) : (
          <Input value={config.var} onChange={(e) => onChange({ var: e.target.value })} />
        )}
      </Form.Item>
      <Form.Item label="Validator">
        <Select
          value={config.validator || 'regex'}
          onChange={(validator) => onChange({ validator })}
          options={[
            { value: 'regex', label: 'Regex' },
            { value: 'date_range', label: 'Date range' },
          ]}
        />
      </Form.Item>
      {config.validator === 'regex' && (
        <Form.Item label="Pattern">
          <Input
            value={config.pattern}
            onChange={(e) => onChange({ pattern: e.target.value })}
            placeholder="e.g. ^[0-9]{6}$"
          />
          <div style={{ marginTop: 8 }}>
            <span style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.45)', display: 'block', marginBottom: 4 }}>
              Common patterns (click to apply):
            </span>
            <Space wrap>
              {COMMON_REGEX_PATTERNS.map((p) => (
                <Tag
                  key={p.value}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onChange({ pattern: p.value })}
                >
                  {p.label}
                </Tag>
              ))}
            </Space>
          </div>
        </Form.Item>
      )}
    </>
  );
}

export function LogicConditionConfig({ config, onChange, nodeOptions }) {
  return (
    <>
      <Form.Item label="Source">
        <Select
          value={config.source || 'last_api_response'}
          onChange={(source) => onChange({ source })}
          options={[
            { value: 'last_api_response', label: 'Last API response' },
            { value: 'vars', label: 'Session variables' },
          ]}
        />
      </Form.Item>
      <Form.Item label="JSON key / path">
        <Input
          value={config.path || config.key}
          onChange={(e) => onChange({ path: e.target.value })}
        />
      </Form.Item>
      <Space wrap>
        <Form.Item label="Operator" style={{ marginBottom: 0 }}>
          <Select
            value={config.operator || 'equals'}
            onChange={(operator) => onChange({ operator })}
            style={{ width: 120 }}
            options={[
              { value: 'equals', label: 'Equals' },
              { value: 'not_equals', label: 'Not equals' },
              { value: 'contains', label: 'Contains' },
            ]}
          />
        </Form.Item>
        <Form.Item label="Value" style={{ marginBottom: 0 }}>
          <Input
            value={config.value}
            onChange={(e) => onChange({ value: e.target.value })}
          />
        </Form.Item>
      </Space>
      <Form.Item label="Next step on success">
        <NodeSelect
          value={config.on_success}
          onChange={(on_success) => onChange({ on_success })}
        />
      </Form.Item>
      <Form.Item label="Next step on failure">
        <NodeSelect
          value={config.on_failure}
          onChange={(on_failure) => onChange({ on_failure })}
        />
      </Form.Item>
    </>
  );
}

export function ApiRequestConfig({ config, onChange, nodeOptions, placeholders = [], varOptions = [] }) {
  const [tab, setTab] = React.useState('general');
  const urlRef = React.useRef(null);
  const bodyRawRef = React.useRef(null);
  const [rawJsonError, setRawJsonError] = React.useState(null);
  const saveMap = config.save_from_api || {};
  const mapEntries = Object.entries(saveMap);
  const bodyMode = config.body_mode || 'key_value';

  const handleFormatJson = () => {
    try {
      // Replace {{placeholders}} with temporary strings so JSON.parse doesn't choke
      const raw = (config.body_raw || '').replace(/{{[^}]+}}/g, (m) => `"__PH_${btoa(m.slice(2, -2))}__"`);
      const parsed = JSON.parse(raw);
      const pretty = JSON.stringify(parsed, null, 2)
        // Restore placeholders
        .replace(/"__PH_([A-Za-z0-9+/=]+)__"/g, (_, b) => `{{${atob(b)}}}`);
      onChange({ body_raw: pretty });
      setRawJsonError(null);
    } catch (e) {
      setRawJsonError('Invalid JSON — fix syntax errors first.');
    }
  };

  const general = (
    <>
      <Form.Item label="Display name">
        <Input
          value={config.display_name}
          onChange={(e) => onChange({ display_name: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Method">
        <Select
          value={config.method || 'GET'}
          onChange={(method) => onChange({ method })}
          options={['GET', 'POST', 'PUT', 'DELETE'].map((m) => ({ value: m, label: m }))}
        />
      </Form.Item>
      <Form.Item label="URL">
        <TextArea
          ref={urlRef}
          rows={3}
          value={config.url}
          onChange={(e) => onChange({ url: e.target.value })}
        />
      </Form.Item>
      <PlaceholderChips
        placeholders={placeholders}
        textareaRef={urlRef}
        onInsert={(p) => insertAtCursor(urlRef, config.url, p, (v) => onChange({ url: v }))}
      />
      <Form.Item label="Success condition">
        <Space wrap>
          <Input
            placeholder="JSON key"
            value={config.success_path}
            onChange={(e) => onChange({ success_path: e.target.value })}
            style={{ width: 120 }}
          />
          <Select
            value={config.success_operator || 'equals'}
            onChange={(success_operator) => onChange({ success_operator })}
            style={{ width: 100 }}
            options={[{ value: 'equals', label: 'Equals' }]}
          />
          <Input
            placeholder="true"
            value={config.success_value}
            onChange={(e) => onChange({ success_value: e.target.value })}
            style={{ width: 80 }}
          />
        </Space>
      </Form.Item>
      <Form.Item label="Next step on success">
        <NodeSelect
          value={config.on_success}
          onChange={(on_success) => onChange({ on_success })}
        />
      </Form.Item>
      <Form.Item label="Next step on failure">
        <NodeSelect
          value={config.on_failure}
          onChange={(on_failure) => onChange({ on_failure })}
        />
      </Form.Item>
    </>
  );

  const isBodyMethod = ['POST', 'PUT'].includes((config.method || 'GET').toUpperCase());

  const bodyParams = config.body_params || [];

  const updateBodyParam = (index, patch) => {
    const next = [...bodyParams];
    next[index] = { ...next[index], ...patch };
    onChange({ body_params: next });
  };

  const removeBodyParam = (index) => {
    onChange({ body_params: bodyParams.filter((_, i) => i !== index) });
  };

  const addBodyParam = () => {
    onChange({ body_params: [...bodyParams, { key: '', value: '', source: 'static' }] });
  };

  const body = (
    <>
      {/* Body mode toggle */}
      <Form.Item label="Body mode">
        <Radio.Group
          value={bodyMode}
          onChange={(e) => onChange({ body_mode: e.target.value })}
          optionType="button"
          buttonStyle="solid"
          size="small"
        >
          <Radio.Button value="key_value">Key-Value</Radio.Button>
          <Radio.Button value="raw_json">Raw JSON</Radio.Button>
        </Radio.Group>
      </Form.Item>

      {bodyMode === 'raw_json' ? (
        /* ── Raw JSON mode ── */
        <Form.Item
          label={
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Raw JSON body
              <Button
                size="small"
                type="dashed"
                onClick={handleFormatJson}
                style={{ fontSize: 11, padding: '0 6px', height: 20, lineHeight: '18px' }}
              >
                ⌥ Format JSON
              </Button>
            </span>
          }
          extra={
            rawJsonError
              ? <span style={{ color: '#ff4d4f' }}>{rawJsonError}</span>
              : 'Write any JSON structure. Use {{variable}} placeholders to inject flow variables at runtime.'
          }
        >
          <Input.TextArea
            ref={bodyRawRef}
            rows={8}
            value={config.body_raw || ''}
            onChange={(e) => { onChange({ body_raw: e.target.value }); setRawJsonError(null); }}
            placeholder={`{\n  "items": [\n    { "productId": "{{product_id}}", "qty": "{{quantity}}" }\n  ],\n  "customerId": "{{customer_id}}"\n}`}
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          />
          <PlaceholderChips
            placeholders={placeholders}
            textareaRef={bodyRawRef}
            onInsert={(p) =>
              insertAtCursor(bodyRawRef, config.body_raw || '', p, (v) => onChange({ body_raw: v }))
            }
          />
        </Form.Item>
      ) : (
        /* ── Key-Value mode ── */
        <Form.Item
          label="Request body"
          extra="Key-value pairs sent as JSON body. Pick 'variable' to use a flow var, or 'static' for a fixed value. Use dot notation for nested keys, e.g. items.productId"
        >
          {bodyParams.map((param, i) => (
            <Flex
              key={i}
              gap={8}
              alignItems="flex-start"
              margin="0 0 8px 0"
              style={{ gap: '8px' }}
            >
              <Input
                placeholder="Key (e.g. items.productId)"
                value={param.key}
                onChange={(e) => updateBodyParam(i, { key: e.target.value })}
                style={{ flex: 1 }}
              />
              <Select
                value={param.source || 'static'}
                onChange={(source) => updateBodyParam(i, { source, value: '' })}
                style={{ width: 100 }}
                options={[
                  { value: 'static', label: 'Static' },
                  { value: 'var', label: 'Variable' },
                ]}
              />
              {param.source === 'var' ? (
                varOptions.length > 0 ? (
                  <Select
                    showSearch
                    allowClear
                    placeholder="Select var"
                    value={param.value || undefined}
                    onChange={(v) => updateBodyParam(i, { value: v || '' })}
                    options={varOptions}
                    style={{ flex: 1 }}
                  />
                ) : (
                  <Input
                    placeholder="e.g. otp"
                    value={param.value}
                    onChange={(e) => updateBodyParam(i, { value: e.target.value })}
                    style={{ flex: 1 }}
                  />
                )
              ) : (
                <Input
                  placeholder="Value"
                  value={param.value}
                  onChange={(e) => updateBodyParam(i, { value: e.target.value })}
                  style={{ flex: 1 }}
                />
              )}
              <Button
                type="text"
                danger
                size="small"
                onClick={() => removeBodyParam(i)}
                style={{ marginTop: 4 }}
              >
                ×
              </Button>
            </Flex>
          ))}
          <Button type="dashed" block onClick={addBodyParam}>
            + Add param
          </Button>
        </Form.Item>
      )}
    </>
  );

  const headerParams = config.header_params || [];

  const updateHeaderParam = (index, patch) => {
    const next = [...headerParams];
    next[index] = { ...next[index], ...patch };
    onChange({ header_params: next });
  };

  const removeHeaderParam = (index) => {
    onChange({ header_params: headerParams.filter((_, i) => i !== index) });
  };

  const addHeaderParam = () => {
    onChange({ header_params: [...headerParams, { key: '', value: '', source: 'static' }] });
  };

  const headers = (
    <Form.Item
      label="Request headers"
      extra="e.g. Authorization: Bearer token, X-Api-Key, Content-Type, etc."
    >
      {headerParams.map((param, i) => (
        <Flex
          key={i}
          gap={8}
          alignItems="flex-start"
          margin="0 0 8px 0"
          style={{ gap: '8px' }}
        >
          <Input
            placeholder="Key (e.g. Authorization)"
            value={param.key}
            onChange={(e) => updateHeaderParam(i, { key: e.target.value })}
            style={{ flex: 1 }}
          />
          <Select
            value={param.source || 'static'}
            onChange={(source) => updateHeaderParam(i, { source, value: '' })}
            style={{ width: 100 }}
            options={[
              { value: 'static', label: 'Static' },
              { value: 'var', label: 'Variable' },
            ]}
          />
          {param.source === 'var' ? (
            varOptions.length > 0 ? (
              <Select
                showSearch
                allowClear
                placeholder="Select var"
                value={param.value || undefined}
                onChange={(v) => updateHeaderParam(i, { value: v || '' })}
                options={varOptions}
                style={{ flex: 1 }}
              />
            ) : (
              <Input
                placeholder="e.g. token"
                value={param.value}
                onChange={(e) => updateHeaderParam(i, { value: e.target.value })}
                style={{ flex: 1 }}
              />
            )
          ) : (
            <Input
              placeholder="Value"
              value={param.value}
              onChange={(e) => updateHeaderParam(i, { value: e.target.value })}
              style={{ flex: 1 }}
            />
          )}
          <Button
            type="text"
            danger
            size="small"
            onClick={() => removeHeaderParam(i)}
            style={{ marginTop: 4 }}
          >
            ×
          </Button>
        </Flex>
      ))}
      <Button type="dashed" block onClick={addHeaderParam}>
        + Add header
      </Button>
    </Form.Item>
  );

  const response = (
    <>
      <Form.Item
        label="Save from API (variable → JSON path)"
        extra={
          <>
            Path is dot notation on the API JSON body. Examples:{' '}
            <Text code>access_token</Text>, <Text code>values</Text> (array for lists),{' '}
            <Text code>data.items</Text>. For Salesforce OrderPicklist use{' '}
            <Text code>values</Text> — not <Text code>label</Text>.
          </>
        }
      >
        {mapEntries.length > 0 ? (
          <Flex gap={8} style={{ marginBottom: 8, fontSize: 12 }} className="text-muted">
            <span style={{ flex: 1 }}>Variable name</span>
            <span style={{ flex: 1 }}>JSON path in response</span>
            <span style={{ width: 24 }} />
          </Flex>
        ) : null}
        {mapEntries.map(([varName, path], i) => (
          <Flex
            key={i}
            gap={8}
            alignItems="flex-start"
            margin="0 0 8px 0"
            style={{ gap: '8px' }}
          >
            <Input
              placeholder="auth_token"
              value={varName}
              onChange={(e) => {
                const next = { ...saveMap };
                delete next[varName];
                next[e.target.value] = path;
                onChange({ save_from_api: next });
              }}
              style={{ flex: 1 }}
            />
            <Input
              placeholder="access_token or values"
              value={path}
              onChange={(e) =>
                onChange({ save_from_api: { ...saveMap, [varName]: e.target.value } })
              }
              style={{ flex: 1 }}
            />
            <Button
              type="text"
              danger
              size="small"
              onClick={() => {
                const next = { ...saveMap };
                delete next[varName];
                onChange({ save_from_api: next });
              }}
              style={{ marginTop: 4 }}
            >
              ×
            </Button>
          </Flex>
        ))}
        <Button
          type="dashed"
          block
          onClick={() =>
            onChange({ save_from_api: { ...saveMap, [`var_${mapEntries.length}`]: '' } })
          }
        >
          Add mapping
        </Button>
      </Form.Item>
    </>
  );

  return (
    <Tabs
      activeKey={tab}
      onChange={setTab}
      items={[
        { key: 'general', label: 'General', children: general },
        ...(isBodyMethod ? [{ key: 'body', label: 'Body', children: body }] : []),
        { key: 'headers', label: 'Headers', children: headers },
        { key: 'response', label: 'Response', children: response },
      ]}
    />
  );
}

export function ListPresetConfig({ config, onChange, varOptions = [], nodeOptions = [] }) {
  const { presets, isLoading } = useListPresets();
  const [showPreview, setShowPreview] = useState(false);

  const presetOptions = useMemo(() => {
    return (presets || []).map((p) => ({
      value: p.id,
      label: p.name,
    }));
  }, [presets]);

  const selectedPreset = useMemo(() => {
    if (!config.preset_id) return null;
    return (presets || []).find((p) => p.id === config.preset_id) || null;
  }, [config.preset_id, presets]);

  const rows = useMemo(() => {
    const configured = Array.isArray(config.rows) ? config.rows : [];
    if (configured.length > 0) {
      return configured;
    }
    return mergeListPresetRows(presetRowsFromPreset(selectedPreset), []);
  }, [config.rows, selectedPreset]);

  const handleSelect = (presetId) => {
    if (!presetId) {
      onChange({ preset_id: null, preset_name: '', rows: [] });
      setShowPreview(false);
      return;
    }
    const preset = (presets || []).find((p) => p.id === presetId);
    const nextRows = mergeListPresetRows(
      presetRowsFromPreset(preset),
      config.rows || []
    );
    onChange({
      preset_id: presetId,
      preset_name: preset?.name || '',
      rows: nextRows,
    });
    setShowPreview(true);
  };

  const previewRows = useMemo(() => {
    if (rows.length > 0) {
      return rows;
    }
    if (!selectedPreset?.rows) return [];
    try {
      const parsed = typeof selectedPreset.rows === 'string'
        ? JSON.parse(selectedPreset.rows)
        : selectedPreset.rows;
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }, [rows, selectedPreset]);

  const updateRow = (index, patch) => {
    const next = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange({ rows: next });
  };

  return (
    <Flex gap={24} alignItems="flex-start" style={{ gap: '24px' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Form.Item
          label={
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              List preset
              {config.preset_id && (
                <Tooltip title={showPreview ? 'Hide preview' : 'Show preview'}>
                  <Button
                    type="text"
                    size="small"
                    shape="circle"
                    icon={showPreview ? <EyeOutlined style={{ color: '#3e79f7' }} /> : <EyeInvisibleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />}
                    onClick={() => setShowPreview(!showPreview)}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  />
                </Tooltip>
              )}
            </span>
          }
        >
          <Select
            showSearch
            allowClear
            loading={isLoading}
            placeholder="Search and select a preset"
            value={config.preset_id || undefined}
            onChange={handleSelect}
            options={presetOptions}
            filterOption={(input, opt) =>
              opt?.label?.toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: '100%' }}
          />
        </Form.Item>

        {rows.length > 0 && (
          <CaptureIncomingField config={config} onChange={onChange} varOptions={varOptions} />
        )}

        {rows.length > 0 && (
          <Form.Item
            extra="When the customer picks a list row, the flow jumps to that step (like template quick reply). Click Save node to wire edges."
            className="mt-2"
          >
            {rows.map((row, i) => (
              <div
                key={`${row.id || row.label}-${i}`}
                style={{
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  padding: '8px 12px',
                  marginBottom: 8,
                }}
              >
                <Flex alignItems="center" gap={8} margin="0 0 12px 0" style={{ gap: '8px' }}>
                  <Text strong>{row.label || row.id || `Option ${i + 1}`}</Text>
                  <ArrowRightOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                  <Text type="secondary">Send this step</Text>
                </Flex>
                <Form.Item style={{ marginBottom: 0 }}>
                  <NodeSelect
                    placeholder="Select next step (template, message, API, …)"
                    value={row.target_node_id}
                    onChange={(target_node_id) =>
                      updateRow(i, { target_node_id: target_node_id || '' })
                    }
                  />
                </Form.Item>
              </div>
            ))}
          </Form.Item>
        )}
      </div>

      {showPreview && selectedPreset && (
        <div style={{ flex: 1, minWidth: 320, maxWidth: 400, position: 'sticky', top: 16 }}>
          <ListItemPreview
            headerType="text"
            open={showPreview}
            header={selectedPreset.header}
            body={selectedPreset.body}
            footer={selectedPreset.footer}
            rows={previewRows}
            buttonText={selectedPreset.button_text}
          />
        </div>
      )}
    </Flex>
  );
}

export function FlowEndConfig({ config, onChange, varOptions = [] }) {
  return (
    <Form.Item label="Clear variables on end">
      <Select
        mode="multiple"
        value={config.clear_vars || []}
        onChange={(clear_vars) => onChange({ clear_vars })}
        options={varOptions}
        placeholder="Select variables to clear"
      />
    </Form.Item>
  );
}

export function SubflowConfig({ config, onChange, varOptions = [], currentFlowId = null }) {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getFlows({ status: 'published' })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data;
        const list = Array.isArray(payload) ? payload : payload?.data || [];
        setFlows(
          list.filter(
            (f) =>
              f.status === 'published' &&
              f.id !== currentFlowId &&
              f.is_active !== false
          )
        );
      })
      .catch(() => {
        if (!cancelled) setFlows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentFlowId]);

  const flowOptions = useMemo(() => {
    const opts = flows.map((f) => ({
      value: f.id,
      label: `${f.name} · v${f.version ?? 1}${f.group_id ? ` (group #${f.group_id})` : ''}`,
    }));
    const selectedId = config.flow_version_id;
    if (
      selectedId &&
      !opts.some((o) => o.value === selectedId) &&
      config.flow_name
    ) {
      opts.unshift({
        value: selectedId,
        label: `${config.flow_name} · #${selectedId}`,
      });
    }
    return opts;
  }, [flows, config.flow_version_id, config.flow_name]);

  const handleFlowSelect = (flowVersionId) => {
    if (!flowVersionId) {
      onChange({ flow_version_id: null, flow_name: '' });
      return;
    }
    const flow = flows.find((f) => f.id === flowVersionId);
    onChange({
      flow_version_id: flowVersionId,
      flow_name: flow?.name || config.flow_name || '',
    });
  };

  const passVars = Array.isArray(config.pass_vars) ? config.pass_vars : [];

  return (
    <>
      <Form.Item
        label="Published flow"
        extra="Customer continues in this flow when this step runs. Only published flows are listed."
      >
        <Select
          showSearch
          allowClear
          loading={loading}
          placeholder="Select a published flow"
          value={config.flow_version_id || undefined}
          onChange={handleFlowSelect}
          options={flowOptions}
          filterOption={(input, opt) =>
            String(opt?.label || '').toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: '100%' }}
        />
      </Form.Item>

      {!loading && flows.length === 0 && !config.flow_version_id && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>
          No other published flows found. Publish a target flow first, then select it here.
        </Text>
      )}

      <Form.Item
        label="Pass variables"
        extra="Copy these session variables from this flow into the subflow when it starts (e.g. branch, category, product)."
      >
        {varOptions.length > 0 ? (
          <Select
            mode="multiple"
            allowClear
            placeholder="Select variables to pass"
            value={passVars}
            onChange={(pass_vars) => onChange({ pass_vars })}
            options={varOptions}
            style={{ width: '100%' }}
          />
        ) : (
          <Select
            mode="tags"
            placeholder="branch, category, product"
            value={passVars}
            onChange={(pass_vars) => onChange({ pass_vars })}
            style={{ width: '100%' }}
          />
        )}
      </Form.Item>
    </>
  );
}

export function HandoffAgentConfig({ config, onChange, placeholders = [] }) {
  const { data: agents = [], isLoading } = useSupportAgents();
  const messageRef = useRef(null);

  const assignMode = config.assign_mode || (config.agent_id ? 'specific' : 'auto');

  const agentOptions = useMemo(() => {
    const opts = (agents || []).map((agent) => {
      const online = agent?.support_agent?.online === 1;
      return {
        value: agent.id,
        label: `${agent.name}${online ? ' · online' : ' · offline'}`,
      };
    });
    const selectedId = config.agent_id;
    if (
      selectedId &&
      !opts.some((o) => o.value === selectedId) &&
      config.agent_name
    ) {
      opts.unshift({
        value: selectedId,
        label: config.agent_name,
      });
    }
    return opts;
  }, [agents, config.agent_id, config.agent_name]);

  const handleAssignModeChange = (mode) => {
    if (mode === 'auto') {
      onChange({ assign_mode: 'auto', agent_id: null, agent_name: '' });
      return;
    }
    onChange({ assign_mode: 'specific' });
  };

  const handleAgentSelect = (agentId) => {
    if (!agentId) {
      onChange({ agent_id: null, agent_name: '' });
      return;
    }
    const agent = (agents || []).find((a) => a.id === agentId);
    onChange({
      agent_id: agentId,
      agent_name: agent?.name || '',
      assign_mode: 'specific',
    });
  };

  return (
    <>
      <Form.Item
        label="Assignment"
        extra="Auto picks an online support agent with the fewest active chats (same as live chat auto-assign)."
      >
        <Radio.Group
          value={assignMode}
          onChange={(e) => handleAssignModeChange(e.target.value)}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <Radio value="auto">Auto assign (recommended)</Radio>
          <Radio value="specific">Choose a specific agent</Radio>
        </Radio.Group>
      </Form.Item>

      {assignMode === 'specific' && (
        <Form.Item label="Support agent">
          <Select
            showSearch
            allowClear
            loading={isLoading}
            placeholder="Select support agent"
            value={config.agent_id || undefined}
            onChange={handleAgentSelect}
            options={agentOptions}
            filterOption={(input, opt) =>
              String(opt?.label || '').toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: '100%' }}
          />
        </Form.Item>
      )}

      {!isLoading && assignMode === 'specific' && agentOptions.length === 0 && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>
          No support agents found. Add agents under Users → Support agents first.
        </Text>
      )}

      <Form.Item
        label="Message to customer (optional)"
        extra="Sent when handoff runs. Leave empty to assign silently."
      >
        <TextArea
          ref={messageRef}
          rows={3}
          value={config.message || ''}
          onChange={(e) => onChange({ message: e.target.value })}
          placeholder="A team member will assist you shortly."
        />
        <PlaceholderChips
          placeholders={placeholders}
          textareaRef={messageRef}
          onInsert={(p) =>
            insertAtCursor(messageRef, config.message || '', p, (next) =>
              onChange({ message: next })
            )
          }
        />
      </Form.Item>

      <Form.Item label="Pause chatbot after handoff" valuePropName="checked">
        <Switch
          checked={config.pause_chatbot !== false}
          onChange={(pause_chatbot) => onChange({ pause_chatbot })}
        />
      </Form.Item>
    </>
  );
}

function PlaceholderChips({ placeholders = [], onInsert, textareaRef }) {
  // Save cursor position before focus is lost (chip click steals focus)
  const savedCursor = React.useRef(null);

  const handleMouseDown = (e, val) => {
    // Prevent the textarea from losing focus so selectionStart stays valid
    e.preventDefault();
    onInsert?.(val);
  };

  const selectOptions = [
    {
      label: 'Contact Info',
      options: [
        { value: '{{contact.name}}', label: '{{contact.name}} (Profile Name)' },
        { value: '{{contact.wa_id}}', label: '{{contact.wa_id}} (WhatsApp ID)' },
      ],
    },
    {
      label: "This Flow's Variables",
      options: placeholders.map((p) => ({
        value: p.startsWith('{{') ? p : `{{${p}}}`,
        label: p.startsWith('{{') ? p : `{{${p}}}`,
      })),
    },
    {
      label: 'Common Session Variables',
      options: [
        { value: '{{vars.otp}}', label: '{{vars.otp}} (OTP Code)' },
        { value: '{{vars.token}}', label: '{{vars.token}} (API Access Token)' },
        { value: '{{vars.branch}}', label: '{{vars.branch}} (Route choice)' },
      ],
    },
    {
      label: 'Last API Response',
      options: [
        { value: '{{api.last.success}}', label: '{{api.last.success}} (Success flag)' },
      ],
    },
  ];

  return (
    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', width: '100%' }}>
      <Space wrap size={4}>
        <Text type="secondary" style={{ fontSize: 11 }}>Quick insert:</Text>
        <Tag
          className="placeholder-chip"
          onMouseDown={(e) => handleMouseDown(e, '{{contact.name}}')}
          style={{ cursor: 'pointer', margin: '2px 0' }}
        >
          {"{{contact.name}}"}
        </Tag>
        {placeholders.slice(0, 2).map((p) => {
          const val = p.startsWith('{{') ? p : `{{${p}}}`;
          return (
            <Tag
              key={p}
              className="placeholder-chip"
              onMouseDown={(e) => handleMouseDown(e, val)}
              style={{ cursor: 'pointer', margin: '2px 0' }}
            >
              {val}
            </Tag>
          );
        })}
      </Space>
      <Select
        placeholder="Insert variable..."
        size="small"
        showSearch
        filterOption={(input, option) =>
          !option?.options && // skip group headers
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
        style={{ width: 160 }}
        dropdownMatchSelectWidth={false}
        onChange={(val) => {
          if (val) {
            onInsert?.(val);
          }
        }}
        value={null}
        options={selectOptions}
      />
    </div>
  );
}

export function renderNodeConfig(type, props) {
  const {
    config,
    onChange,
    nodeOptions,
    placeholders = [],
    varOptions = [],
    nodeId = null,
    definition = null,
    currentFlowId = null,
  } = props;
  switch (type) {
    case 'trigger.keyword':
      return <TriggerKeywordConfig config={config} onChange={onChange} />;
    case 'trigger.regex':
      return <TriggerRegexConfig config={config} onChange={onChange} />;
    case 'trigger.default':
      return <p style={{ color: '#8c8c8c' }}>Runs for new chats when no keyword matches.</p>;
    case 'message.text':
      return (
        <MessageTextConfig config={config} onChange={onChange} placeholders={placeholders} />
      );
    case 'message.template':
    case 'message.interactive_buttons':
      return (
        <MessageTemplateConfig
          config={config}
          onChange={onChange}
          nodeOptions={nodeOptions}
          varOptions={varOptions}
        />
      );
    case 'message.list_preset':
      return (
        <ListPresetConfig
          config={config}
          onChange={onChange}
          varOptions={varOptions}
          nodeOptions={nodeOptions}
        />
      );
    case 'message.list_dynamic':
      return (
        <ListDynamicConfig
          config={config}
          onChange={onChange}
          varOptions={varOptions}
          nodeOptions={nodeOptions}
        />
      );
    case 'logic.ask':
      return (
        <LogicAskConfig config={config} onChange={onChange} varOptions={varOptions} />
      );
    case 'logic.validate':
      return (
        <LogicValidateConfig config={config} onChange={onChange} varOptions={varOptions} />
      );
    case 'logic.condition':
      return (
        <LogicConditionConfig
          config={config}
          onChange={onChange}
          nodeOptions={nodeOptions}
        />
      );
    case 'logic.set_var':
      return (
        <>
          <Form.Item label="Variable">
            <Input value={config.var} onChange={(e) => onChange({ var: e.target.value })} />
          </Form.Item>
          <Form.Item label="Value">
            <Input value={config.value} onChange={(e) => onChange({ value: e.target.value })} />
          </Form.Item>
        </>
      );
    case 'logic.route_by_var':
      return (
        <RouteByVarConfig
          config={config}
          onChange={onChange}
          varOptions={varOptions}
          nodeOptions={nodeOptions}
          nodeId={nodeId}
          definition={definition}
        />
      );
    case 'integration.api':
      return (
        <ApiRequestConfig
          config={config}
          onChange={onChange}
          nodeOptions={nodeOptions}
          placeholders={placeholders}
          varOptions={varOptions}
        />
      );
    case 'flow.subflow':
      return (
        <SubflowConfig
          config={config}
          onChange={onChange}
          varOptions={varOptions}
          currentFlowId={currentFlowId}
        />
      );
    case 'flow.end':
      return (
        <FlowEndConfig config={config} onChange={onChange} varOptions={varOptions} />
      );
    case 'handoff.agent':
      return (
        <HandoffAgentConfig
          config={config}
          onChange={onChange}
          placeholders={placeholders}
        />
      );
    case 'ai.classify':
      return (
        <AiClassifyConfig
          config={config}
          onChange={onChange}
          varOptions={varOptions}
          nodeOptions={nodeOptions}
          nodeId={nodeId}
          definition={definition}
        />
      );
    case 'ai.extract':
      return (
        <AiExtractConfig config={config} onChange={onChange} varOptions={varOptions} />
      );
    case 'ai.reply':
      return (
        <AiReplyConfig
          config={config}
          onChange={onChange}
          varOptions={varOptions}
          placeholders={placeholders}
        />
      );
    case 'ai.generate':
      return (
        <AiGenerateConfig config={config} onChange={onChange} varOptions={varOptions} />
      );
    default:
      return (
        <TextArea
          rows={8}
          value={JSON.stringify(config, null, 2)}
          onChange={(e) => {
            try {
              onChange(JSON.parse(e.target.value));
            } catch {
              /* */
            }
          }}
        />
      );
  }
}
